import type {SupportedMobileSdkHostApp} from './types';

// Currently this is based on the ShortVersionString from iOS
// https://developer.apple.com/documentation/bundleresources/information_property_list/cfbundleshortversionstring
// IMPORTANT: Any changes to this regex should be copied to frontend/embedded-theming/src/utils/checkForMobileSdk.ts
const stripeVersionRegex =
  /stripe-(ios|android|react_native)\/(\d+\.\d+\.\d+)$/;

export type MobileSdkUserAgentType = ReturnType<typeof mapHostAppToOs>;

export const isMobileSdkUserAgentType = (
  value: unknown,
): value is MobileSdkUserAgentType =>
  value === 'ios' ||
  value === 'android' ||
  value === 'react_native' ||
  value === 'web_test';

export type MobileSdkVersionInfo = {
  version: string;
  userAgentType: MobileSdkUserAgentType;
};

export function getMobileSdkVersion(
  hostApp: SupportedMobileSdkHostApp,
): MobileSdkVersionInfo {
  const userAgent = getUserAgent();
  if (!userAgent) {
    throw new Error('User agent was not found.');
  }

  const expectedOs = mapHostAppToOs(hostApp);

  const match = userAgent.match(stripeVersionRegex);
  if (match) {
    if (match[1] !== expectedOs) {
      throw new Error('User agent did not match the expected Platform.');
    }

    return {version: match[2], userAgentType: expectedOs};
  }

  if (expectedOs === 'web_test') {
    return {version: '99.99.0', userAgentType: 'web_test'};
  }

  throw new Error('User agent was not found in the expected format');
}

export function getUserAgent(): string | null | undefined {
  const userAgent =
    // eslint-disable-next-line @stripe-internal/embedded/no-restricted-globals
    typeof window !== 'undefined' && navigator && window.navigator.userAgent;
  return userAgent || undefined;
}

const mapHostAppToOs = (hostApp: SupportedMobileSdkHostApp) => {
  switch (hostApp) {
    case 'platform_ios':
      return 'ios';
    // We actually only use this value later, but making sure we cover all types
    case 'stripe_dashboard_ios':
      return 'ios';
    case 'platform_android':
      return 'android';
    case 'stripe_dashboard_android':
      return 'react_native';
    case 'platform_react_native':
      return 'react_native';
    case 'web_test':
      return 'web_test';
    default:
      throw new Error('Unsupported host app');
  }
};
